import React, { useState, useEffect } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Typography,
  Paper,
  Avatar,
  Stack,
  Box,
  CircularProgress,
  LinearProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Assessment,
  Analytics,
  Notes,
  Favorite
} from '@mui/icons-material';

// Dashboard Components
import TabPanel from './components/TabPanel';
import AnalyticsTab from './tabs/AnalyticsTab';
import NotesTab from './tabs/NotesTab';
import FavoritesTab from './tabs/FavoritesTab';

// Import the production dashboard content as the overview
import ProductionDashboardContent from './components/ProductionDashboardContent';

// Data and Utils
import { generateMockDashboardData, generateMockNotes } from './data/mockData';

// Services
import { useFavorites } from '../../context/FavoritesContext';
import apiClient from '../../services/apiClient';

const TabbedProductionDashboard = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState({ 
    title: '', 
    content: '', 
    propertyId: '', 
    tags: [] 
  });
  const { } = useFavorites();

  useEffect(() => {
    Promise.all([
      fetchDashboardData(),
      fetchNotes()
    ]);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/properties/dashboard-analytics');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(generateMockDashboardData());
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiClient.get('/properties/notes');
      setNotes(response.data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setNotes(generateMockNotes());
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNoteDialogOpen = (note = null) => {
    if (note) {
      setCurrentNote(note);
    } else {
      setCurrentNote({ title: '', content: '', propertyId: '', tags: [] });
    }
    setNoteDialogOpen(true);
  };

  const handleNoteDialogClose = () => {
    setNoteDialogOpen(false);
    setCurrentNote({ title: '', content: '', propertyId: '', tags: [] });
  };

  const handleNoteSave = async () => {
    try {
      if (currentNote.id) {
        await apiClient.put(`/properties/notes/${currentNote.id}`, currentNote);
      } else {
        await apiClient.post('/properties/notes', currentNote);
      }
      await fetchNotes();
    } catch (error) {
      console.error('Error saving note:', error);
    }
    handleNoteDialogClose();
  };

  const handleNoteChange = (updatedNote) => {
    setCurrentNote(updatedNote);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ mb: 2, color: 'primary.main' }} />
          <Typography variant="h6" color="text.secondary">
            Loading dashboard analytics...
          </Typography>
          <LinearProgress sx={{ width: 300, mt: 2 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Enhanced Header with Tabs */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          mb: 3
        }}
      >
        <Box sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha(theme.palette.common.white, 0.2), 
                width: 56, 
                height: 56 
              }}
            >
              <Analytics sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Investment Analytics Dashboard
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
                Comprehensive property investment insights and market analytics
              </Typography>
            </Box>
          </Stack>
        </Box>
        
        {/* Tab Navigation */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ 
            px: 4,
            '& .MuiTab-root': { 
              color: alpha(theme.palette.common.white, 0.7),
              fontWeight: 'medium',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: 64,
              '&:hover': {
                color: alpha(theme.palette.common.white, 0.9)
              }
            },
            '& .Mui-selected': { 
              color: 'white !important',
              fontWeight: 'bold'
            },
            '& .MuiTabs-indicator': { 
              backgroundColor: 'white',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab 
            icon={<Assessment />} 
            label="Overview" 
            iconPosition="start"
            sx={{ mr: 2 }}
          />
          <Tab 
            icon={<Analytics />} 
            label="Analytics" 
            iconPosition="start"
            sx={{ mr: 2 }}
          />
          <Tab 
            icon={<Notes />} 
            label="Notes" 
            iconPosition="start"
            sx={{ mr: 2 }}
          />
          <Tab 
            icon={<Favorite />} 
            label="Favorites" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <ProductionDashboardContent 
          dashboardData={dashboardData} 
          onRefresh={fetchDashboardData}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <AnalyticsTab dashboardData={dashboardData} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <NotesTab 
          notes={notes}
          noteDialogOpen={noteDialogOpen}
          currentNote={currentNote}
          onNoteDialogOpen={handleNoteDialogOpen}
          onNoteDialogClose={handleNoteDialogClose}
          onNoteSave={handleNoteSave}
          onNoteChange={handleNoteChange}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <FavoritesTab />
      </TabPanel>
    </Container>
  );
};

export default TabbedProductionDashboard;
